import { useMemo, useState } from 'react';

import { StatusDot } from '@/components/atoms';

import { computeSeoScore } from './seoScore';
import styles from './SeoPanel.module.css';

interface SeoPanelProps {
  title: string;
  excerpt: string;
  contentHtml: string;
  featuredMediaId: number | null;
  slug?: string | null;
  /** PAA real do SERP (origem do artigo) — R6: cada pergunta vira H3 com 1 clique. */
  paa?: string[];
  /**
   * When the editor wants to react to PAA picks it passes this
   * callback — the panel only owns the keyword input + score.
   */
  onInsertH3?: (question: string) => void;
}

export function SeoPanel({
  title,
  excerpt,
  contentHtml,
  featuredMediaId,
  slug,
  paa,
  onInsertH3,
}: SeoPanelProps) {
  const [keyword, setKeyword] = useState('');
  const [paaQuery, setPaaQuery] = useState('');

  const result = useMemo(
    () =>
      computeSeoScore({
        title,
        excerpt,
        contentHtml,
        featuredMediaId,
        slug,
        keyword,
      }),
    [title, excerpt, contentHtml, featuredMediaId, slug, keyword],
  );

  const scoreTone =
    result.score >= 80 ? 'ok' : result.score >= 50 ? 'warn' : 'crit';

  return (
    <aside className={styles.root} aria-label="SEO panel">
      <header className={styles.head}>
        <h2 className={styles.title}>SEO</h2>
        <div className={`${styles.score} ${styles[`score-${scoreTone}`]}`}>
          <span className={styles.scoreValue}>{result.score}</span>
          <span className={styles.scoreMax}>/100</span>
        </div>
      </header>

      <div className={styles.bar}>
        <div
          className={styles.barFill}
          style={{
            width: `${result.score}%`,
            background:
              scoreTone === 'ok'
                ? 'var(--ok)'
                : scoreTone === 'warn'
                  ? 'var(--warn)'
                  : 'var(--crit)',
          }}
        />
      </div>

      <ul className={styles.checks}>
        {result.checks.map((c) => (
          <li key={c.key} className={styles.check}>
            <StatusDot
              state={
                c.status === 'ok' ? 'ok' : c.status === 'warn' ? 'warn' : 'crit'
              }
            />
            <span className={styles.checkLabel}>{c.label}</span>
            {c.detail && (
              <span className={styles.checkDetail}>{c.detail}</span>
            )}
          </li>
        ))}
      </ul>

      <div className={styles.section}>
        <label className={styles.fieldLabel}>Keyword focal</label>
        <input
          className={styles.input}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="ex.: ebola Congo"
        />
        <p className={styles.hint}>
          Activa checks de keyword no título / 1.º parágrafo / density.
        </p>
      </div>

      <div className={styles.section}>
        <label className={styles.fieldLabel}>People Also Asked</label>
        {paa && paa.length > 0 ? (
          <>
            <p className={styles.hint}>
              Perguntas reais do público (SERP, da origem do artigo) — clica para
              inserir como H3:
            </p>
            {paa.map((q) => (
              <button
                key={q}
                type="button"
                className={styles.addH3}
                onClick={() => onInsertH3?.(q)}
                disabled={!onInsertH3}
              >
                + {q}
              </button>
            ))}
          </>
        ) : (
          <>
            <p className={styles.hint}>
              Sem PAA da origem. Adiciona uma pergunta manualmente:
            </p>
            <input
              className={styles.input}
              value={paaQuery}
              onChange={(e) => setPaaQuery(e.target.value)}
              placeholder="pergunta do leitor…"
            />
            {paaQuery.trim() && onInsertH3 && (
              <button
                type="button"
                className={styles.addH3}
                onClick={() => onInsertH3(paaQuery.trim())}
              >
                + Adicionar &quot;{paaQuery.trim()}&quot; como H3
              </button>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
